import { useDebouncedEffect } from "@react-hookz/web";
import {
    useInfiniteQuery,
    useQueryClient,
    type InfiniteData,
    type QueryClient,
    type QueryKey,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation } from "react-router";

import type { Page } from "@/api/api.types";
import { getConversations } from "@/api/chat/chat";
import type {
    ConversationSortKey,
    IChatConversationListItem,
} from "@/api/chat/chat.types";
import type { IClientMinimal } from "@/api/clients/clients.types";
import { activityTime } from "@/utils/chatConversations";

import { getChatHistoryTab, type ChatHistoryTab } from "../chatRoutes";
import { groupLabel } from "./chatHistoryDates";
import { CHAT_HISTORY_KEY } from "./chatHistoryKeys";

const PAGE_SIZE = 30;
const TITLE_SORT_LABEL = "Title A–Z";

export const CHAT_HISTORY_SORT_OPTIONS = [
    { value: "recently_active", label: "Most recent" },
    { value: "least_recently_active", label: "Oldest first" },
    { value: "title_asc", label: TITLE_SORT_LABEL },
] as const satisfies readonly { value: ConversationSortKey; label: string }[];

export type ChatHistorySort =
    (typeof CHAT_HISTORY_SORT_OPTIONS)[number]["value"];

export type ChatHistoryBranch =
    | "loading"
    | "error"
    | "empty-filtered"
    | "empty-tab"
    | "populated";

export interface ChatHistorySection {
    label: string;
    rows: IChatConversationListItem[];
}

export interface NextPageSentinelState {
    show: boolean;
    resetKey: string;
    ref: (node?: Element | null) => void;
}

export interface UseChatHistoryResult {
    tab: ChatHistoryTab;
    search: string;
    setSearch: (value: string) => void;
    q: string;
    clearSearch: () => void;
    client: IClientMinimal | null;
    setClient: (client: IClientMinimal | null) => void;
    sort: ChatHistorySort;
    setSort: (sort: ChatHistorySort) => void;
    clearFilters: () => void;
    branch: ChatHistoryBranch;
    rows: IChatConversationListItem[];
    firstLaterRowId: number | undefined;
    sections: ChatHistorySection[];
    total: number;
    now: Date;
    isPlaceholderData: boolean;
    refetch: () => void;
    nextPageSentinel: NextPageSentinelState;
    isFetchingNextPage: boolean;
    isFetchNextPageError: boolean;
    fetchNextPage: () => void;
}

type HistoryPages = InfiniteData<Page<IChatConversationListItem>, number>;

/** Flatten the pages, keeping the first copy of a row that offset paging returned twice. */
function selectRows(data: HistoryPages) {
    const seen = new Set<number>();
    const rows: IChatConversationListItem[] = [];
    for (const page of data.pages) {
        for (const row of page.items) {
            if (seen.has(row.id)) continue;
            seen.add(row.id);
            rows.push(row);
        }
    }
    return { rows, total: data.pages[data.pages.length - 1].total };
}

function toSections(
    rows: IChatConversationListItem[],
    sort: ChatHistorySort,
    now: Date
): ChatHistorySection[] {
    if (sort === "title_asc") {
        return rows.length > 0 ? [{ label: TITLE_SORT_LABEL, rows }] : [];
    }
    const sections: ChatHistorySection[] = [];
    for (const row of rows) {
        const label = groupLabel(activityTime(row), now);
        const current = sections[sections.length - 1];
        if (current?.label === label) {
            current.rows.push(row);
        } else {
            sections.push({ label, rows: [row] });
        }
    }
    return sections;
}

/** Cancel the fetches of the matching history lists that have pages, then cut each to its first page, keeping its age and invalidation; leave a list with no pages fetching. */
function trimToFirstPage(
    queryClient: QueryClient,
    queryKey: QueryKey = [CHAT_HISTORY_KEY]
) {
    const lists = queryClient
        .getQueryCache()
        .findAll({ queryKey })
        .map((query) => ({ query, wasInvalidated: query.state.isInvalidated }));
    void queryClient.cancelQueries({
        queryKey,
        predicate: (query) => query.state.data !== undefined,
    });
    for (const { query, wasInvalidated } of lists) {
        const data = query.state.data as HistoryPages | undefined;
        if (!data) continue;
        queryClient.setQueryData<HistoryPages>(
            query.queryKey,
            {
                pages: data.pages.slice(0, 1),
                pageParams: data.pageParams.slice(0, 1),
            },
            { updatedAt: query.state.dataUpdatedAt }
        );
        if (wasInvalidated) {
            void queryClient.invalidateQueries({
                queryKey: query.queryKey,
                exact: true,
                refetchType: "none",
            });
        }
    }
}

/** Cut a history list holding later pages to its first page as it is invalidated, before the refetch reads its pages, unless it is the list on screen and a later row is in view. */
function trimOnInvalidate(
    queryClient: QueryClient,
    laterRowsInView: () => boolean
) {
    return queryClient.getQueryCache().subscribe((event) => {
        if (event.type !== "updated" || event.action.type !== "invalidate") {
            return;
        }
        const { query } = event;
        const data = query.state.data as HistoryPages | undefined;
        if (
            query.queryKey[0] === CHAT_HISTORY_KEY &&
            (data?.pages.length ?? 0) > 1 &&
            !(query.isActive() && laterRowsInView())
        ) {
            trimToFirstPage(queryClient, query.queryKey);
        }
    });
}

/** Load the history list; `laterRowsInView` reports whether a row past the first page is in view or scrolled past. */
export function useChatHistory(
    laterRowsInView: () => boolean
): UseChatHistoryResult {
    const { pathname } = useLocation();
    const tab = getChatHistoryTab(pathname) ?? "chats";

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useDebouncedEffect(() => setDebouncedSearch(search), [search], 300);
    const q = debouncedSearch.trim();

    const [client, setClient] = useState<IClientMinimal | null>(null);
    const [sort, setSort] = useState<ChatHistorySort>("recently_active");
    const clientId = client?.id;

    const {
        data,
        isError,
        isPlaceholderData,
        isFetching,
        hasNextPage,
        isFetchingNextPage,
        isFetchNextPageError,
        fetchNextPage,
        refetch,
    } = useInfiniteQuery({
        queryKey: [CHAT_HISTORY_KEY, tab, q, clientId ?? null, sort],
        queryFn: (context) =>
            getConversations(
                context.pageParam,
                PAGE_SIZE,
                {
                    q: q || undefined,
                    sort_by: sort,
                    ...(tab === "shared" && {
                        access_mode: "collaborator" as const,
                    }),
                },
                clientId,
                undefined,
                {
                    // A read signal lets query-core abort the fetch when its last
                    // observer leaves, so a cold fetch must not read it.
                    signal:
                        context.client.getQueryData(context.queryKey) ===
                        undefined
                            ? undefined
                            : context.signal,
                }
            ),
        initialPageParam: 1,
        getNextPageParam: (last) =>
            last.page < last.pages ? last.page + 1 : undefined,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (previous, previousQuery) =>
            previousQuery?.queryKey[1] === tab ? previous : undefined,
        select: selectRows,
    });

    const rows = data?.rows ?? [];
    const branch: ChatHistoryBranch = !data
        ? isError
            ? "error"
            : "loading"
        : rows.length > 0
          ? "populated"
          : q || client
            ? "empty-filtered"
            : "empty-tab";
    const showSentinel = branch === "populated" && !isPlaceholderData;

    const fetchMore = useCallback(
        () => void fetchNextPage({ cancelRefetch: false }),
        [fetchNextPage]
    );
    const { ref: observerRef, inView, entry } = useInView();
    const sentinelNode = useRef<Element | null>(null);
    const sentinelRef = useCallback(
        (node?: Element | null) => {
            sentinelNode.current = node ?? null;
            observerRef(node ?? null);
        },
        [observerRef]
    );
    useEffect(() => {
        if (
            showSentinel &&
            inView &&
            entry?.target === sentinelNode.current &&
            hasNextPage &&
            !isFetching &&
            !isFetchNextPageError
        ) {
            fetchMore();
        }
    }, [
        showSentinel,
        entry,
        inView,
        hasNextPage,
        isFetching,
        isFetchNextPageError,
        fetchMore,
    ]);

    const queryClient = useQueryClient();
    useEffect(
        () => trimOnInvalidate(queryClient, laterRowsInView),
        [queryClient, laterRowsInView]
    );
    useEffect(() => () => trimToFirstPage(queryClient), [queryClient]);

    const [rowsSort, setRowsSort] = useState(sort);
    if (data && !isPlaceholderData && rowsSort !== sort) {
        setRowsSort(sort);
    }

    const now = new Date();

    const clearSearch = () => {
        setSearch("");
        setDebouncedSearch("");
    };

    return {
        tab,
        search,
        setSearch,
        q,
        clearSearch,
        client,
        setClient,
        sort,
        setSort,
        clearFilters: () => {
            clearSearch();
            setClient(null);
        },
        branch,
        rows,
        firstLaterRowId: rows[PAGE_SIZE]?.id,
        sections: toSections(rows, isPlaceholderData ? rowsSort : sort, now),
        total: data?.total ?? 0,
        now,
        isPlaceholderData,
        refetch: () => void refetch(),
        nextPageSentinel: {
            show: showSentinel,
            resetKey: JSON.stringify([tab, q, clientId ?? null, sort]),
            ref: sentinelRef,
        },
        isFetchingNextPage,
        isFetchNextPageError,
        fetchNextPage: fetchMore,
    };
}
